function clamp01(value) {
  return Math.min(1, Math.max(0, Number.isFinite(value) ? value : 0));
}

function editable(target) {
  const tag = target?.tagName?.toLowerCase();
  return target?.isContentEditable || tag === 'input' || tag === 'textarea' || tag === 'select';
}

export function createProgress({ reducedMotion = false } = {}) {
  let enabled = false;
  let autoSpeed = null;
  let touchY = null;
  let lastTouchX = null;
  const listeners = [];
  const state = {
    t: 0,
    target: 0,
    get enabled() {
      return enabled;
    },
    set enabled(value) {
      enabled = Boolean(value);
    },
    get gliding() {
      return autoSpeed !== null;
    },
    update(dt) {
      const elapsed = Math.max(0, Number.isFinite(dt) ? dt : 0);
      if (enabled && autoSpeed !== null) state.target = clamp01(state.target + autoSpeed * elapsed);
      if (reducedMotion) state.t = state.target;
      else state.t += (state.target - state.t) * (1 - Math.exp(-4.0 * elapsed));
      if (Math.abs(state.target - state.t) < 0.000001) state.t = state.target;
      if (state.target >= 1) autoSpeed = null;
      return state.t;
    },
    seek(value) {
      state.t = clamp01(value);
      state.target = state.t;
    },
    toggleGlide() {
      // Roughly eight minutes end to end: long enough to feel like a passage,
      // while scroll/touch always lets the pilot take control.
      autoSpeed = autoSpeed === null ? 0.0021 : null;
      return autoSpeed !== null;
    },
    stopGlide() {
      autoSpeed = null;
    },
    destroy() {
      listeners.splice(0).forEach(([type, handler, options]) => window.removeEventListener(type, handler, options));
    },
  };

  function nudge(amount) {
    if (!enabled) return;
    autoSpeed = null;
    state.target = clamp01(state.target + amount);
    if (reducedMotion) state.t = state.target;
  }

  function listen(type, handler, options) {
    window.addEventListener(type, handler, options);
    listeners.push([type, handler, options]);
  }

  listen(
    'wheel',
    (event) => {
      const mode = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? 600 : 1;
      nudge(event.deltaY * mode * 0.000075);
    },
    { passive: true },
  );

  listen(
    'touchstart',
    (event) => {
      if (event.touches?.length !== 1) {
        touchY = null;
        lastTouchX = null;
        return;
      }
      touchY = event.touches[0].clientY;
      lastTouchX = event.touches[0].clientX;
    },
    { passive: true },
  );

  listen(
    'touchmove',
    (event) => {
      if (touchY === null || event.touches?.length !== 1) return;
      const nextY = event.touches[0].clientY;
      const nextX = event.touches[0].clientX;
      const vertical = touchY - nextY;
      const horizontal = Math.abs(nextX - lastTouchX);
      if (Math.abs(vertical) >= horizontal * 0.55) nudge(vertical * 0.00042);
      touchY = nextY;
      lastTouchX = nextX;
    },
    { passive: true },
  );

  listen(
    'touchend',
    () => {
      touchY = null;
      lastTouchX = null;
    },
    { passive: true },
  );
  listen(
    'touchcancel',
    () => {
      touchY = null;
      lastTouchX = null;
    },
    { passive: true },
  );

  listen('keydown', (event) => {
    if (!enabled || editable(event.target) || event.altKey || event.ctrlKey || event.metaKey) return;
    const amounts = {
      ArrowDown: 0.008,
      ArrowRight: 0.008,
      PageDown: 0.032,
      ArrowUp: -0.008,
      ArrowLeft: -0.008,
      PageUp: -0.032,
    };
    if (Object.hasOwn(amounts, event.key)) {
      nudge(amounts[event.key]);
      event.preventDefault();
    }
  });

  return state;
}
